import { type LedgerEntry, type RequestLedger, RequestOriginKind } from "./models";
import { openingsOf } from "./requestOrigins";

/** How a closed slot left the request, for the card's footer ("2 filled · 1 lowered"). */
export enum SlotRemoval {
	Filled = "filled",
	Lowered = "lowered",
	Canceled = "canceled",
	Merged = "merged",
	/** A removal with no recorded cause ("unknown"), the rest of a merge ("count adjusted"), or a merge that lowered the count. */
	Other = "other",
}

/** The footer's order. */
export const SLOT_REMOVALS: readonly SlotRemoval[] = [SlotRemoval.Filled, SlotRemoval.Lowered, SlotRemoval.Canceled, SlotRemoval.Merged, SlotRemoval.Other];

/** Opened, closed (by how) and still open. `opened − Σ removed = stillOpen`, because the entries add up to it. */
export type LedgerTally = { opened: number; removed: Readonly<Record<SlotRemoval, number>>; stillOpen: number };

/** Which removal a negative entry is. A kind that only ever opens slots can't be negative; one that is breaks the API's contract. */
export const removalOf = (entry: LedgerEntry): SlotRemoval => {
	switch (entry.details.kind) {
		case RequestOriginKind.Filled:
			return SlotRemoval.Filled;
		case RequestOriginKind.CountLowered:
		case RequestOriginKind.AgencySyncLowered:
			return SlotRemoval.Lowered;
		case RequestOriginKind.Canceled:
			return SlotRemoval.Canceled;
		case RequestOriginKind.MergedAway:
			return SlotRemoval.Merged;
		case RequestOriginKind.Unknown:
		case RequestOriginKind.CountAdjusted:
		// A merge sets the target's count to what the admin's screen sent, which can be lower than it was.
		case RequestOriginKind.MergedIn:
			return SlotRemoval.Other;
		case RequestOriginKind.CallOff:
		case RequestOriginKind.TimeOff:
		case RequestOriginKind.Deactivation:
		case RequestOriginKind.AutoPost:
		case RequestOriginKind.FromRecommendation:
		case RequestOriginKind.PostedByPerson:
		case RequestOriginKind.PostedByAnnie:
		case RequestOriginKind.ReplaceRequest:
		case RequestOriginKind.SwapRequest:
		case RequestOriginKind.ReplacingAgency:
		case RequestOriginKind.Agency:
		case RequestOriginKind.AgencyCanceled:
		case RequestOriginKind.CountRaised:
		case RequestOriginKind.NotRecorded:
			throw new Error(`A ${entry.details.kind} entry only opens slots, but its delta is ${entry.delta}`);
		default: {
			const unhandled: never = entry.details;
			throw new Error(`Unhandled origin ${JSON.stringify(unhandled)}`);
		}
	}
};

export const ledgerTally = (ledger: RequestLedger): LedgerTally => {
	const removed: Record<SlotRemoval, number> = {
		[SlotRemoval.Filled]: 0,
		[SlotRemoval.Lowered]: 0,
		[SlotRemoval.Canceled]: 0,
		[SlotRemoval.Merged]: 0,
		[SlotRemoval.Other]: 0,
	};
	let opened = 0;
	for (const entry of ledger.entries) {
		if (entry.delta > 0) opened += entry.delta;
		else if (entry.delta < 0) removed[removalOf(entry)] += -entry.delta;
	}
	return { opened, removed, stillOpen: ledger.open_slots_now };
};

const containsUnknown = (entries: readonly LedgerEntry[]): boolean =>
	entries.some(
		(entry) =>
			entry.details.kind === RequestOriginKind.Unknown || (entry.details.kind === RequestOriginKind.MergedIn && containsUnknown(entry.details.entries)),
	);

/** Whether any change, a merged-in request's included, has no cause InHouse could find. */
export const hasUnknown = (ledger: RequestLedger): boolean => containsUnknown(ledger.entries);

/**
 * The entry the request is known by at rest (its glyph, analytics' `kind`): the first that opened slots. A request whose
 * only entry opened none (a pre-launch request that is no longer open) is known by that entry.
 */
export const leadingEntry = (ledger: RequestLedger): LedgerEntry => {
	const [firstOpening] = openingsOf(ledger.entries);
	if (firstOpening !== undefined) return firstOpening;
	const [first] = ledger.entries;
	if (first === undefined) throw new Error(`Request ${ledger.request_id} has an empty ledger`);
	return first;
};

/** How many other requests the same action or run created or changed, for "with 2 other shifts"; 0 when it was alone or unknown. A batch always holds the request itself, so its size is at least 1. */
export const otherShiftsInBatch = (entry: LedgerEntry): number =>
	// DEFAULT: no recorded run (a pre-launch or unstamped change) reads as alone, so no "with N other shifts" is claimed.
	entry.batch_size === null ? 0 : entry.batch_size - 1;
